import type { OrderData, OrderEventData } from '../types/chartData'
import { pairKeyOf, type PairKey } from '../utils/orderPairing'
import { PENDING_TYPE_LABEL } from '../utils/pendingOrders'

/**
 * Click tooltip listing the orders on one bar. Same content as the old
 * hand-built DOM tooltip inside GraphCandle, but as a component styled with v1
 * tokens instead of inline hex.
 *
 * PnL colours stay literal (#00C853 / #FF1744 / #9E9E9E) so they match the
 * on-chart marker arrows exactly — those are canvas fills and can't read CSS
 * custom properties.
 */

const NEUTRAL = '#9E9E9E'
const WIN = '#00C853'
const LOSS = '#FF1744'

export interface BacktestOrderTooltipProps {
  orders: OrderData[]
  /** Click point within the chart container, in px. */
  point: { x: number; y: number }
  /** Container width, so the card can flip to the left of the cursor. */
  containerWidth: number
  /** Decimal places for prices, from the symbol's point size. */
  precision: number
  /**
   * position key (see pairKeyOf) -> ticket number. Omitted where there are no ticket numbers to
   * show (the Backtest tab), which is why every order's `#n` is optional rather than falling
   * back to something — an invented number is worse than none.
   */
  tickets?: ReadonlyMap<PairKey, number>
  /** Scale-ins and partial exits on this bar (the walkthrough only). */
  activity?: OrderEventData[]
}

export default function BacktestOrderTooltip({
  orders,
  point,
  containerWidth,
  precision,
  tickets,
  activity = [],
}: BacktestOrderTooltipProps) {
  if (orders.length === 0 && activity.length === 0) return null

  const opened = orders.filter((o) => o.eventType === 'opened')
  const closed = orders.filter((o) => o.eventType === 'closed')

  /**
   * The trade's number, as the trades pane calls it.
   *
   * Keyed on the position's entry time so a closing event finds the same number its entry has —
   * the two are separate events with separate ids, and an exit card reading a different number
   * from the entry card on the same trade would be the whole point of having numbers, lost.
   */
  const ticketOf = (o: OrderData) => {
    const key = pairKeyOf(o)
    const n = key === null ? undefined : tickets?.get(key)
    return n == null ? null : (
      <span className="ml-1.5 tabular-nums text-text-secondary">#{n}</span>
    )
  }

  // Card width is fixed so the flip decision needs no measurement pass.
  const WIDTH = 280
  const left = point.x + 16 + WIDTH > containerWidth ? Math.max(0, point.x - WIDTH - 10) : point.x + 16

  const px = (v: number) => v.toFixed(precision)

  return (
    <div
      className="absolute z-10 pointer-events-none rounded-md border border-border bg-popover text-caption text-foreground shadow-popover px-2.5 py-2 leading-relaxed"
      style={{ left, top: Math.max(0, point.y - 20), width: WIDTH }}
    >
      <div className="font-semibold pb-1 mb-1 border-b border-border">
        Orders ({orders.length + activity.length})
      </div>

      {opened.length > 0 && (
        <>
          <div className="text-eyebrow font-semibold text-muted-foreground mb-0.5">Opened</div>
          {opened.map((o) => (
            <div key={`open-${o.orderId}`} className="mb-1">
              <span className="font-semibold" style={{ color: NEUTRAL }}>
                {o.type === 'BUY' ? '▲' : '▼'} {o.type}
              </span>
              {ticketOf(o)}
              {o.volume != null && (
                <span className="ml-1 text-eyebrow text-text-tertiary">
                  {o.volume.toFixed(2)} lot
                </span>
              )}
              <div className="flex gap-2.5 mt-px text-eyebrow text-text-secondary">
                <span>Entry: {px(o.price)}</span>
                <span>SL: {px(o.sl)}</span>
                <span>TP: {px(o.tp)}</span>
              </div>
            </div>
          ))}
        </>
      )}

      {closed.length > 0 && (
        <>
          {opened.length > 0 && <div className="border-t border-border my-1" />}
          <div className="text-eyebrow font-semibold text-muted-foreground mb-0.5">Closed</div>
          {closed.map((o) => {
            const pnl = o.pnl ?? 0
            const color = pnl > 0 ? WIN : LOSS
            return (
              <div key={`closed-${o.orderId}`} className="mb-1">
                <span className="font-semibold" style={{ color }}>
                  {o.type === 'BUY' ? '▲' : '▼'} {o.type}
                </span>
                {ticketOf(o)}
                <span className="ml-1.5 font-semibold" style={{ color }}>
                  {pnl > 0 ? '+' : ''}
                  {pnl.toFixed(2)}
                </span>
                {o.volume != null && (
                  <span className="ml-1 text-eyebrow text-text-tertiary">
                    {o.volume.toFixed(2)} lot
                  </span>
                )}
                <div className="flex gap-2.5 mt-px text-eyebrow text-text-secondary">
                  <span>
                    {px(o.entryPrice ?? o.price)} {'→'} {px(o.exitPrice ?? o.price)}
                  </span>
                  {o.exitReason && <span>{o.exitReason}</span>}
                </div>
              </div>
            )
          })}
        </>
      )}

      {activity.length > 0 && (
        <>
          {orders.length > 0 && <div className="border-t border-border my-1" />}
          <div className="text-eyebrow font-semibold text-muted-foreground mb-0.5">Activity</div>
          {activity.map((a) => {
            if (a.kind === 'position_reduced') {
              const color = a.pnl > 0 ? WIN : LOSS
              return (
                <div key={`act-${a.eventId}`} className="mb-1">
                  <span className="font-semibold" style={{ color }}>
                    Partial exit {a.closedVolume.toFixed(2)} @ {px(a.exitPrice)}
                  </span>
                  <span className="ml-1.5 font-semibold" style={{ color }}>
                    {a.pnl > 0 ? '+' : ''}
                    {a.pnl.toFixed(2)}
                  </span>
                  <div className="text-eyebrow text-text-secondary">{a.remainingVolume.toFixed(2)} lot left</div>
                </div>
              )
            }
            if (a.kind === 'order_filled') {
              return (
                <div key={`act-${a.eventId}`} className="mb-1">
                  <span className="font-semibold" style={{ color: NEUTRAL }}>
                    {a.effect === 'increase' ? 'Scale-in' : 'Fill'} {PENDING_TYPE_LABEL[a.orderType]}{' '}
                    {a.fillVolume.toFixed(2)} @ {px(a.fillPrice)}
                  </span>
                </div>
              )
            }
            return null
          })}
        </>
      )}
    </div>
  )
}
